<script setup>
import {onMounted, ref, defineProps, defineEmits, defineExpose} from "vue";
import LuceneExpressionFixButton
  from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/LuceneExpressionFixButton.vue";
import {t} from "@/controllerLK/GlobusLocalization";
onMounted(() => {
  updateEditorValue(props.modelValue);
  updateEditor();
});

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  maxLength: {
    type: Number,
    default: 1200
  }
});

const queryDivTextAreaRef = ref();
let editor;

function updateEditorValue(value) {
  editor = queryDivTextAreaRef.value;
  queryDivTextAreaRef.value.innerHTML = value;
}

function getTextSegments(element) {
  const textSegments = [];
  Array.from(element.childNodes).forEach((node) => {
    switch(node.nodeType) {
      case Node.TEXT_NODE:
        textSegments.push({text: node.nodeValue, node});
        break;

      case Node.ELEMENT_NODE:
        textSegments.splice(textSegments.length, 0, ...(getTextSegments(node)));
        break;

      default:
        throw new Error(`Unexpected node type: ${node.nodeType}`);
    }
  });
  return textSegments;
}

function updateEditor() {
  const sel = window.getSelection();
  const textSegments = getTextSegments(editor);
  const textContent = textSegments.map(({text}) => text).join("");
  let anchorIndex = null;
  let focusIndex = null;
  let currentIndex = 0;
  textSegments.forEach(({text, node}) => {
    if (node === sel.anchorNode) {
      anchorIndex = currentIndex + sel.anchorOffset;
    }
    if (node === sel.focusNode) {
      focusIndex = currentIndex + sel.focusOffset;
    }
    currentIndex += text.length;
  });

  editor.innerHTML = renderText(textContent);

  restoreSelection(anchorIndex, focusIndex);
  emit("update:modelValue", textContent);
}

function restoreSelection(absoluteAnchorIndex, absoluteFocusIndex) {
  const sel = window.getSelection();
  const textSegments = getTextSegments(editor);
  let anchorNode = editor;
  let anchorIndex = 0;
  let focusNode = editor;
  let focusIndex = 0;
  let currentIndex = 0;
  textSegments.forEach(({text, node}) => {
    const startIndexOfNode = currentIndex;
    const endIndexOfNode = startIndexOfNode + text.length;
    if (startIndexOfNode <= absoluteAnchorIndex && absoluteAnchorIndex <= endIndexOfNode) {
      anchorNode = node;
      anchorIndex = absoluteAnchorIndex - startIndexOfNode;
    }
    if (startIndexOfNode <= absoluteFocusIndex && absoluteFocusIndex <= endIndexOfNode) {
      focusNode = node;
      focusIndex = absoluteFocusIndex - startIndexOfNode;
    }
    currentIndex += text.length;
  });

  sel.setBaseAndExtent(anchorNode, anchorIndex, focusNode, focusIndex);
}

function renderText(text) {
  return text
      .split(/(\s+|\u200B)/)
      .map((word) =>
          ["AND", "OR", "NOT", "BEFORE", "AFTER"].includes(word)
              ? `<span style="color: #1F9AFF; font-weight: 700">${word}</span>`
              : word
      )
      .join("");
}

function insertLineBreak() {
  const sel = window.getSelection();
  const range = sel.getRangeAt(0);

  // Создаем перенос строки
  const newLine = document.createTextNode("\n");

  range.deleteContents();
  range.insertNode(newLine);

  // Обновляем диапазон и курсор
  const newRange = document.createRange();
  newRange.setStart(newLine, 1);
  newRange.setEnd(newLine, 1);

  sel.removeAllRanges();
  sel.addRange(newRange);

  // Вызываем обновление редактора
  setTimeout(updateEditor, 0);
}

function replaceQuery (value) {
  updateEditorValue(value);
  updateEditor();
}

function insertText(text) {
  const sel = window.getSelection();
  if (!sel.rangeCount) return;

  const range = sel.getRangeAt(0);

  // Определяем парные символы и текст для вставки
  const pairedSymbols = {
    "\"\"": { text: " \"\" ", cursorOffset: 2 },
    "()": { text: " () ", cursorOffset: 2 }
  };

  let insertData;
  if (pairedSymbols[text]) {
    insertData = pairedSymbols[text];
  } else {
    insertData = { text: ` ${text} `, cursorOffset: text.length + 2 };
  }

  // Удаляем выделенный текст (если есть)
  range.deleteContents();

  // Вставляем новый текст
  const textNode = document.createTextNode(insertData.text);
  range.insertNode(textNode);

  // Устанавливаем курсор
  const newRange = document.createRange();
  newRange.setStart(textNode, insertData.cursorOffset);
  newRange.setEnd(textNode, insertData.cursorOffset);

  sel.removeAllRanges();
  sel.addRange(newRange);

  // Обновляем редактор
  setTimeout(updateEditor, 0);
}

// Обработчик ввода
function handleKeyDown(e) {
  // Разрешаем: удаление, навигацию, выделение
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab"];
  if (allowedKeys.includes(e.key)) return;

  // Разрешаем Ctrl/Cmd комбинации (копировать, вырезать, выделить)
  if (e.ctrlKey || e.metaKey) return;

  const textSegments = getTextSegments(queryDivTextAreaRef.value);
  const currentLength = textSegments.map(({text}) => text).join("").length;

  // Блокируем ввод если достигнуто максимальное кол-во символов
  if (currentLength >= props.maxLength) {
    e.preventDefault();
  }
}

// Обработчик вставки текста
function handlePaste(e) {                                                                                                                                                                                                                                                                                             e.preventDefault();

  const sel = window.getSelection();
  if (!sel.rangeCount) return;

  const textSegments = getTextSegments(queryDivTextAreaRef.value);
  const currentLength = textSegments.map(({text}) => text).join("").length;
  const selectedLength = sel.toString().length;

  const availableSpace = props.maxLength - (currentLength - selectedLength);
  if (availableSpace <= 0) return;

  const pastedText = e.clipboardData.getData('text');
  const trimmedText = pastedText.slice(0, availableSpace);

  const range = sel.getRangeAt(0);
  range.deleteContents();

  const textNode = document.createTextNode(trimmedText);
  range.insertNode(textNode);

  // Устанавливаем курсор в конец вставленного текста
  const newRange = document.createRange();
  newRange.setStart(textNode, trimmedText.length);
  newRange.setEnd(textNode, trimmedText.length);
  sel.removeAllRanges();
  sel.addRange(newRange);

  // setTimeout нужен чтобы updateEditor корректно прочитал позицию курсора
  setTimeout(updateEditor, 0);
}

defineExpose({
  insertText
});

</script>

<template>
  <div class="content-wrapper">
    <div
      ref="queryDivTextAreaRef"
      spellcheck="false"
      class="scroll content-editable"
      contenteditable="true"
      :custom-placeholder="t('news_page_rubrics_edit_popup.input.query_param.placeholder')"
      @keydown="handleKeyDown"
      @paste="handlePaste"
      @keydown.enter.prevent="insertLineBreak"
      @input.prevent="updateEditor"
    />
    <LuceneExpressionFixButton
      :query="props.modelValue"
      @fix-query="value => replaceQuery(value)"
    />
  </div>
</template>

<style lang="scss">

.content-wrapper {
  background-color: #FFFFFF;
  height: 100%;
  padding-right: 5px;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.content-editable {
  width: 100%;
  height: 100%;
  color: #7B87A5;
  line-height: 19.6px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background-color: var(--background-bg-white);
  border-radius: 6px;
  padding: 16px 16px 12px 16px;
  position: relative;

  &:empty:before{
    content: attr(custom-placeholder);
    pointer-events: none;
    display: block; /* For Firefox */
  }
}
</style>